import { useState, useEffect, useCallback } from 'react'
import {
  UserGroupIcon,
  ClipboardCopyIcon,
  CheckIcon,
  PlusIcon,
  LogoutIcon,
} from '@heroicons/react/outline'
import { BaseModal } from './BaseModal'
import {
  createRoom,
  getUserRooms,
  joinRoomByCode,
  leaveRoom,
  Room,
  getRoomRankings,
  RoomRanking,
  RoomMember,
  hasVotedToday,
  voteForUser,
  getRoomMembers,
} from '../../lib/rooms'
import { Difficulty } from '../../hooks/gameReducer'
import { DifficultyList } from '../lists/DifficultyList'

interface Props {
  isOpen: boolean
  handleClose: () => void
  userId: string
  difficulty: Difficulty
}

type ViewMode = 'list' | 'create' | 'join' | 'details'

export const RoomModal = ({
  isOpen,
  handleClose,
  userId,
  difficulty: initialDifficulty,
}: Props) => {
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [rooms, setRooms] = useState<Room[]>([])
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null)
  const [roomRankings, setRoomRankings] = useState<RoomRanking[]>([])
  const [roomMembers, setRoomMembers] = useState<RoomMember[]>([])
  const [hasVoted, setHasVoted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [selectedDifficulty, setSelectedDifficulty] =
    useState<Difficulty>(initialDifficulty)

  // Create room state
  const [roomName, setRoomName] = useState('')
  const [maxMembers, setMaxMembers] = useState(20)
  const [isPrivate, setIsPrivate] = useState(false)

  // Join room state
  const [inviteCode, setInviteCode] = useState('')

  // Copy state
  const [copied, setCopied] = useState(false)

  const loadUserRooms = useCallback(async () => {
    try {
      setLoading(true)
      const userRooms = await getUserRooms(userId)
      setRooms(userRooms)
    } catch (err) {
      setError('Hiba a szobák betöltésekor')
    } finally {
      setLoading(false)
    }
  }, [userId])

  const loadRoomDetails = useCallback(
    async (roomId: string) => {
      try {
        const rankings = await getRoomRankings(roomId, selectedDifficulty)
        setRoomRankings(rankings)

        const members = await getRoomMembers(roomId)
        setRoomMembers(members)

        const voted = await hasVotedToday(roomId, userId)
        setHasVoted(voted)
      } catch (err) {
        console.error('Error loading room details:', err)
      }
    },
    [userId, selectedDifficulty]
  )

  useEffect(() => {
    if (isOpen && viewMode === 'list') {
      loadUserRooms()
    }
  }, [isOpen, viewMode, loadUserRooms])

  useEffect(() => {
    if (selectedRoom) {
      loadRoomDetails(selectedRoom.id)
    }
  }, [selectedRoom, loadRoomDetails])

  useEffect(() => {
    setSelectedDifficulty(initialDifficulty)
  }, [initialDifficulty, isOpen])

  const handleCreateRoom = async () => {
    if (!roomName.trim()) {
      setError('Add meg a szoba nevét')
      return
    }

    try {
      setLoading(true)
      setError('')
      await createRoom(roomName, userId, maxMembers, isPrivate)
      setSuccess('Szoba sikeresen létrehozva!')
      setRoomName('')
      setMaxMembers(20)
      setIsPrivate(false)
      setTimeout(() => {
        setViewMode('list')
        loadUserRooms()
      }, 1000)
    } catch (err) {
      setError('Hiba a szoba létrehozásakor')
    } finally {
      setLoading(false)
    }
  }

  const handleJoinRoom = async () => {
    if (!inviteCode.trim()) {
      setError('Add meg a meghívó kódot')
      return
    }

    try {
      setLoading(true)
      setError('')
      const room = await joinRoomByCode(inviteCode.toUpperCase(), userId)

      if (!room) {
        setError('Érvénytelen meghívó kód')
        return
      }

      setSuccess('Sikeresen csatlakoztál a szobához!')
      setInviteCode('')
      setTimeout(() => {
        setViewMode('list')
        loadUserRooms()
      }, 1000)
    } catch (err: any) {
      if (err.message === 'Room is full') {
        setError('A szoba betelt')
      } else {
        setError('Hiba a csatlakozáskor')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleLeaveRoom = async (roomId: string) => {
    if (!window.confirm('Biztosan ki szeretnél lépni ebből a szobából?')) {
      return
    }

    try {
      setLoading(true)
      await leaveRoom(roomId, userId)
      setSuccess('Kiléptél a szobából')
      setSelectedRoom(null)
      setViewMode('list')
      loadUserRooms()
    } catch (err) {
      setError('Hiba a kilépéskor')
    } finally {
      setLoading(false)
    }
  }

  const handleVote = async (votedForId: string) => {
    if (!selectedRoom) return

    try {
      const success = await voteForUser(selectedRoom.id, userId, votedForId)

      if (success) {
        setSuccess('Szavazat leadva!')
        setHasVoted(true)
        loadRoomDetails(selectedRoom.id)
      } else {
        setError('Ma már szavaztál')
      }
    } catch (err) {
      setError('Hiba a szavazáskor')
    }
  }

  const copyInviteLink = (code: string) => {
    const url = `${window.location.origin}?room=${code}`
    navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const renderList = () => (
    <div className="space-y-4">
      <div className="flex gap-2">
        <button
          onClick={() => setViewMode('create')}
          className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center justify-center gap-2"
        >
          <PlusIcon className="w-5 h-5" />
          Új szoba
        </button>
        <button
          onClick={() => setViewMode('join')}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center justify-center gap-2"
        >
          <UserGroupIcon className="w-5 h-5" />
          Csatlakozás
        </button>
      </div>

      {rooms.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-300">
          Még nem vagy tagja egyetlen szobának sem
        </div>
      ) : (
        <div className="space-y-2">
          {rooms.map((room) => (
            <div
              key={room.id}
              onClick={() => {
                setSelectedRoom(room)
                setViewMode('details')
              }}
              className="border dark:border-gray-600 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
            >
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold dark:text-white">
                      {room.name}
                    </h3>
                    {room.isPrivate && (
                      <span className="text-xs bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">
                        🔒 Privát
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {room.members.length} / {room.maxMembers} tagok
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    copyInviteLink(room.inviteCode)
                  }}
                  className="text-blue-600 hover:text-blue-700 dark:text-blue-400"
                >
                  {copied ? (
                    <CheckIcon className="w-5 h-5 text-green-600" />
                  ) : (
                    <ClipboardCopyIcon className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )

  const renderCreate = () => (
    <div className="space-y-4">
      <button
        onClick={() => setViewMode('list')}
        className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
      >
        ← Vissza
      </button>

      <div>
        <label className="block text-sm font-medium mb-2 dark:text-white">
          Szoba neve
        </label>
        <input
          type="text"
          value={roomName}
          onChange={(e) => setRoomName(e.target.value)}
          className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          placeholder="pl. Barátok"
          maxLength={50}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2 dark:text-white">
          Maximum tagok
        </label>
        <input
          type="number"
          value={maxMembers}
          onChange={(e) =>
            setMaxMembers(
              Math.min(100, Math.max(2, parseInt(e.target.value) || 20))
            )
          }
          className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          min="2"
          max="100"
        />
      </div>

      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="isPrivate"
          checked={isPrivate}
          onChange={(e) => setIsPrivate(e.target.checked)}
          className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
        />
        <label htmlFor="isPrivate" className="text-sm dark:text-white">
          Privát szoba
          <span className="block text-xs text-gray-500 dark:text-gray-400">
            Csak meghívó linkkel lehet csatlakozni, nem jelenik meg a listákban
          </span>
        </label>
      </div>

      <button
        onClick={handleCreateRoom}
        disabled={loading}
        className="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:bg-gray-400"
      >
        {loading ? 'Létrehozás...' : 'Szoba létrehozása'}
      </button>
    </div>
  )

  const renderJoin = () => (
    <div className="space-y-4">
      <button
        onClick={() => setViewMode('list')}
        className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
      >
        ← Vissza
      </button>

      <div>
        <label className="block text-sm font-medium mb-2 dark:text-white">
          Meghívó kód
        </label>
        <input
          type="text"
          value={inviteCode}
          onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
          className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white uppercase"
          placeholder="pl. ABC12345"
          maxLength={8}
        />
      </div>

      <button
        onClick={handleJoinRoom}
        disabled={loading}
        className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
      >
        {loading ? 'Csatlakozás...' : 'Csatlakozás szobához'}
      </button>
    </div>
  )

  const renderDetails = () => {
    if (!selectedRoom) return null

    return (
      <div className="space-y-4">
        <button
          onClick={() => {
            setSelectedRoom(null)
            setViewMode('list')
          }}
          className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
        >
          ← Vissza
        </button>

        <div className="border-b dark:border-gray-600 pb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-bold dark:text-white">
              {selectedRoom.name}
            </h3>
            {selectedRoom.isPrivate && (
              <span className="text-xs bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">
                🔒 Privát
              </span>
            )}
          </div>
          <div className="flex items-center justify-between mt-2">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Meghívó kód:{' '}
              <span className="font-mono font-bold">
                {selectedRoom.inviteCode}
              </span>
            </p>
            <button
              onClick={() => copyInviteLink(selectedRoom.inviteCode)}
              className="text-blue-600 hover:text-blue-700 dark:text-blue-400"
            >
              {copied ? (
                <CheckIcon className="w-5 h-5 text-green-600" />
              ) : (
                <ClipboardCopyIcon className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        {/* Difficulty selector */}
        <div className="flex items-center justify-between py-2 border-b dark:border-gray-600">
          <span className="text-sm font-medium dark:text-white">Szóhossz:</span>
          <DifficultyList
            selected={selectedDifficulty}
            onChange={(value) => setSelectedDifficulty(value)}
          />
        </div>

        <div>
          <h4 className="font-semibold mb-2 dark:text-white">Rangsor</h4>
          {roomRankings.length === 0 ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Még nincsenek eredmények
            </p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {roomRankings.map((ranking, index) => (
                <div
                  key={ranking.userId}
                  className="flex items-center justify-between p-2 border dark:border-gray-600 rounded"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-lg w-8 dark:text-white">
                      {index + 1}.
                    </span>
                    <div>
                      <p className="font-medium dark:text-white">
                        {ranking.displayName || 'Anonim'}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {ranking.weeklyScore} pont ({ranking.dailyPoints} napi
                        pont)
                      </p>
                    </div>
                  </div>
                  {ranking.userId !== userId && !hasVoted && (
                    <button
                      onClick={() => handleVote(ranking.userId)}
                      className="text-xs bg-yellow-500 text-white px-2 py-1 rounded hover:bg-yellow-600"
                    >
                      Szavazat
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          {hasVoted && (
            <p className="text-xs text-green-600 dark:text-green-400 mt-2">
              ✓ Ma már szavaztál
            </p>
          )}
        </div>

        {/* Members list */}
        <div>
          <h4 className="font-semibold mb-2 dark:text-white">
            Tagok ({roomMembers.length})
          </h4>
          {roomMembers.length === 0 ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Nincsenek tagok
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {roomMembers.map((member) => (
                <span
                  key={member.userId}
                  className="text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded"
                >
                  {member.displayName || 'Anonim'}
                </span>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={() => handleLeaveRoom(selectedRoom.id)}
          disabled={loading}
          className="w-full bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:bg-gray-400 flex items-center justify-center gap-2"
        >
          <LogoutIcon className="w-5 h-5" />
          Kilépés a szobából
        </button>
      </div>
    )
  }

  return (
    <BaseModal title="Szobák" isOpen={isOpen} handleClose={handleClose}>
      <div className="mt-4">
        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-800/50 text-red-700 dark:text-red-100 rounded">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-100 dark:bg-green-800/50 text-green-700 dark:text-green-100 rounded">
            {success}
          </div>
        )}

        {viewMode === 'list' && renderList()}
        {viewMode === 'create' && renderCreate()}
        {viewMode === 'join' && renderJoin()}
        {viewMode === 'details' && renderDetails()}
      </div>
    </BaseModal>
  )
}
